// src/components/Store/secretaryConsultationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from "react-toastify";
import Unauthorized from '../Admin/Unauthorized';
import BaseUrl from '../../BaseUrl';

const headers = Unauthorized.Actionsauthorized();

const API_URL = BaseUrl.authurl
const consulturl = BaseUrl.consultationurl
const BASE_URL = BaseUrl.hmsurl
const scheduleurl = BaseUrl.schedulesurl
const InvoiceUrl = BaseUrl.invoiceurl

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Helper function to safely extract array from API response
const safeExtractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.$values && Array.isArray(data.$values)) return data.$values;
  return [];
};

// Async thunks
export const fetchDepartments = createAsyncThunk(
  'secretaryConsultation/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}Departments/GetAllDepartments`,
        { headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getdocbydeptid = createAsyncThunk(
  'secretaryConsultation/getdocbydeptid',
  async (deptId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}Auth/GetDoctorsByDepartmentId?DepartmentId=${deptId}&Authorized=true`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const GetDoctorSlotsForTheWeek = createAsyncThunk(
  'secretaryConsultation/GetDoctorSlotsForTheWeek',
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${scheduleurl}DoctorSchedules/GetDoctorSlotsForTheWeek?DoctorId=${doctorId}`,
        { headers: getAuthHeaders() }
      );
      console.log(response.data)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const GetPatientsbySearch = createAsyncThunk(
  'secretaryConsultation/GetPatientsbySearch',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}Auth/GetPatients?id=${encodeURIComponent(searchTerm)}`,
        { headers: getAuthHeaders() }
      );
      console.log(response.data)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addPatient = createAsyncThunk(
  'secretaryConsultation/addPatient',
  async (patientData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}Auth/AddPatient`,
        patientData,
        { headers: getAuthHeaders() }
      );
      toast.success('Patient registered successfully!');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to register patient';
      toast.error(message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPatientByMRN = createAsyncThunk(
  'secretaryConsultation/fetchPatientByMRN',
  async (mrn, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}Patients/${mrn}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addConsultation = createAsyncThunk(
  'secretaryConsultation/addConsultation',
  async (consultationData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${consulturl}Consultations/AddNewConsultation`,
        consultationData,
        { headers: getAuthHeaders() }
      );
      toast.success('Consultation booked successfully!');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to book consultation';
      toast.error(message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addAdmission = createAsyncThunk(
  'secretaryConsultation/addAdmission',
  async (admissionData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}Admissions`, admissionData, { 
        headers: { 
          'accept': 'text/plain', 
          'Content-Type': 'application/json' 
        } 
      });
      console.log(response.data, 'Admission response');
      toast.success(`Admission booked successfully!`);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to book admission';
      toast.error(message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPaymentModes = createAsyncThunk(
  'secretaryConsultation/fetchPaymentModes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${InvoiceUrl}PaymentTypeMasters`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addInsuranceAPI = createAsyncThunk(
  'secretaryConsultation/addInsuranceAPI',
  async ({ mrn, insuranceData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}Auth/AddInsurance?mrn=${mrn}`, insuranceData,
        { headers: getAuthHeaders() }
      );
      toast.success('Insurance added successfully!');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add insurance';
      toast.error(message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFloors = createAsyncThunk(
  'secretaryConsultation/fetchFloors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}Floors`,
        { headers: { 'accept': 'text/plain' } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRoomsByFloor = createAsyncThunk(
  'secretaryConsultation/fetchRoomsByFloor',
  async (floorId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}Rooms/GetRoomsByFloor/${floorId}`,
        { headers: { 'accept': 'text/plain' } }
      );
      return { floorId, rooms: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBedsByRoom = createAsyncThunk(
  'secretaryConsultation/fetchBedsByRoom',
  async (roomId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}Beds/GetBedsByRoom/${roomId}`,
        { headers: { 'accept': 'text/plain' } }
      );
      return { roomId, beds: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Helper function to get initial patient state
const getInitialPatientState = () => ({
  contactDetails: {
    name: '',
    phone: '',
    email: '',
    address: '',
    countrycode: '',
    addressDetails: {}
  },
  patientDetails: {
    dob: '',
    gender: '',
    height: '',
    weight: '',
    bloodtype: '',
    allergies: ''
  },
  insuranceDetails: {
    InsuranceProvider: '',
    PolicyNumber: '',
    IssueDate: '',
    InsuranceExpiry: ''
  },
  patientMRN: null,
  patientDisplayId: null,
});

const initialState = {
  ...getInitialPatientState(),
  addedInsurance: [],
  patientPolicies: [],
  
  // Consultation booking
  departments: [],
  selectedDepartment: null,
  doctors: [],
  selectedDoctor: null,
  consultationDate: null,
  consultationTime: null,
  consultationtype: 'out_patient',
  doctorSlots: [],
  selectedSlot: null,
  
  // Admission details
  admissionType: null,
  admissionDate: null,
  admissionReason: null,
  selectedFloorId: null,
  selectedRoomId: null,
  selectedBedId: null,
  selectedRoomCategory: null,
  admissionFee: 0, // ADDED: Admission fee based on room type/category
  
  // Payment
  paymentMode: '',
  paymentModes: [],
  
  // Floors/Rooms/Beds data
  floors: [],
  roomsData: {},
  bedsData: {},
  
  // UI state
  loading: false,
  error: null,
  patients: []
};

const secretaryConsultationSlice = createSlice({
  name: 'secretaryConsultation',
  initialState,
  reducers: {
    setContactDetails: (state, action) => {
      state.contactDetails = action.payload;
    },
    setPatientDetails: (state, action) => {
      state.patientDetails = action.payload;
    },
    setInsuranceDetails: (state, action) => {
      state.insuranceDetails = action.payload;
    },
    setPatientMRN: (state, action) => {
      state.patientMRN = action.payload;
    },
    setPatientDisplayId: (state, action) => {
      state.patientDisplayId = action.payload;
    },
    setPatientIdentifiers: (state, action) => {
      const { mrn, displayId } = action.payload;
      if (mrn !== undefined) state.patientMRN = mrn;
      if (displayId !== undefined) state.patientDisplayId = displayId;
    },
    setAddedInsurance: (state, action) => {
      state.addedInsurance = action.payload;
    },
    setPatientPolicies: (state, action) => {
      state.patientPolicies = action.payload;
    },
    setSelectedDepartment: (state, action) => {
      state.selectedDepartment = action.payload;
    },
    setSelectedDoctor: (state, action) => {
      state.selectedDoctor = action.payload;
      state.doctorSlots = [];
      state.selectedSlot = null;
    },
    setConsultationDate: (state, action) => {
      state.consultationDate = action.payload;
    },
    setConsultationTime: (state, action) => {
      state.consultationTime = action.payload;
    },
    setConsultationType: (state, action) => {
      state.consultationtype = action.payload;
    },
    setSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
      if (action.payload?.TimeSlot) {
        state.consultationTime = action.payload.TimeSlot;
      }
      if (action.payload?.date) {
        state.consultationDate = action.payload.date;
      }
    },
    setAdmissionType: (state, action) => {
      state.admissionType = action.payload;
    },
    setAdmissionDate: (state, action) => {
      state.admissionDate = action.payload;
    },
    setAdmissionReason: (state, action) => {
      state.admissionReason = action.payload;
    },
    // ADDED: Set admission fee
    setAdmissionFee: (state, action) => {
      state.admissionFee = action.payload;
    },
    setSelectedFloorId: (state, action) => {
      state.selectedFloorId = action.payload;
      state.selectedRoomId = null;
      state.selectedBedId = null;
      state.admissionFee = 0; // Reset fee when floor changes
    },
    setSelectedRoomId: (state, action) => {
      state.selectedRoomId = action.payload;
      state.selectedBedId = null;
      state.admissionFee = 0; // Reset fee when room changes (will be recalculated)
    },
    setSelectedBedId: (state, action) => {
      state.selectedBedId = action.payload;
    },
    setSelectedRoomCategory: (state, action) => {
      state.selectedRoomCategory = action.payload;
    },
    setPaymentMode: (state, action) => {
      state.paymentMode = action.payload;
    },
    resetConsultation: (state) => {
      // Reset consultation booking data
      state.selectedDepartment = null;
      state.selectedDoctor = null;
      state.consultationDate = null;
      state.consultationTime = null;
      state.doctorSlots = [];
      state.selectedSlot = null;
      
      // Reset admission data
      state.admissionType = null;
      state.admissionDate = null;
      state.admissionReason = null;
      state.selectedFloorId = null;
      state.selectedRoomId = null;
      state.selectedBedId = null;
      state.selectedRoomCategory = null;
      state.admissionFee = 0; // Reset admission fee
      
      // Reset payment data
      state.paymentMode = '';
      
      // Clear any errors
      state.error = null;
    },
    resetPatientStepDetails: (state) => {
      const newPatientState = getInitialPatientState();
      state.contactDetails = newPatientState.contactDetails;
      state.patientDetails = newPatientState.patientDetails;
      state.insuranceDetails = newPatientState.insuranceDetails;
      state.patientMRN = newPatientState.patientMRN;
      state.patientDisplayId = newPatientState.patientDisplayId;
      state.addedInsurance = [];
      state.patientPolicies = [];
    },
    resetAllBookingData: (state) => {
      // Reset consultation data
      state.selectedDepartment = null;
      state.selectedDoctor = null;
      state.consultationDate = null;
      state.consultationTime = null;
      state.doctorSlots = [];
      state.selectedSlot = null;
      state.admissionType = null;
      state.admissionDate = null;
      state.admissionReason = null;
      state.selectedFloorId = null;
      state.selectedRoomId = null;
      state.selectedBedId = null;
      state.selectedRoomCategory = null;
      state.admissionFee = 0; // Reset admission fee
      state.paymentMode = '';
      
      // Reset patient data
      const newPatientState = getInitialPatientState();
      state.contactDetails = newPatientState.contactDetails;
      state.patientDetails = newPatientState.patientDetails;
      state.insuranceDetails = newPatientState.insuranceDetails;
      state.patientMRN = newPatientState.patientMRN;
      state.patientDisplayId = newPatientState.patientDisplayId;
      state.addedInsurance = [];
      state.patientPolicies = [];
      
      // Clear errors
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Departments
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.loading = false;
        state.departments = safeExtractArray(action.payload);
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Doctors by Department
      .addCase(getdocbydeptid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getdocbydeptid.fulfilled, (state, action) => {
        state.loading = false;
        state.doctors = safeExtractArray(action.payload);
      })
      .addCase(getdocbydeptid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Doctor Slots
      .addCase(GetDoctorSlotsForTheWeek.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetDoctorSlotsForTheWeek.fulfilled, (state, action) => {
        state.loading = false;
        state.doctorSlots = safeExtractArray(action.payload?.Slots || action.payload);
      })
      .addCase(GetDoctorSlotsForTheWeek.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search Patients
      .addCase(GetPatientsbySearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetPatientsbySearch.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = safeExtractArray(action.payload);
      })
      .addCase(GetPatientsbySearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Patient
      .addCase(addPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.patientMRN = action.payload?.mrn || action.payload?.Id;
        if (action.payload?.DisplayId) {
          state.patientDisplayId = action.payload.DisplayId;
        }
      })
      .addCase(addPatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Patient by MRN
      .addCase(fetchPatientByMRN.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientByMRN.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.patientDetails = {
            dob: action.payload.Dob || '',
            gender: action.payload.Gender || '',
            height: action.payload.Height || '',
            weight: action.payload.Weight || '',
            bloodtype: action.payload.BloodType || '',
            allergies: action.payload.Allergies || ''
          };
          state.addedInsurance = safeExtractArray(action.payload.InsuranceProfiles);
          
          if (state.addedInsurance.length > 0) {
            const firstInsurance = state.addedInsurance[0];
            state.insuranceDetails = {
              InsuranceProvider: firstInsurance.InsuranceProvider || firstInsurance.insuranceProvider || '',
              PolicyNumber: firstInsurance.PolicyNumber || firstInsurance.policyNumber || '',
              IssueDate: firstInsurance.IssueDate || '',
              InsuranceExpiry: firstInsurance.InsuranceExpiry || ''
            };
          }
        }
      })
      .addCase(fetchPatientByMRN.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Consultation
      .addCase(addConsultation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addConsultation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addConsultation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Admission
      .addCase(addAdmission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAdmission.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addAdmission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Payment Modes
      .addCase(fetchPaymentModes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentModes.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentModes = safeExtractArray(action.payload);
      })
      .addCase(fetchPaymentModes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.paymentModes = [];
      })

      // Add Insurance
      .addCase(addInsuranceAPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addInsuranceAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.addedInsurance.push(action.payload);
      })
      .addCase(addInsuranceAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Floors
      .addCase(fetchFloors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFloors.fulfilled, (state, action) => {
        state.loading = false;
        state.floors = safeExtractArray(action.payload);
      })
      .addCase(fetchFloors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Rooms by Floor
      .addCase(fetchRoomsByFloor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoomsByFloor.fulfilled, (state, action) => {
        state.loading = false;
        const rooms = safeExtractArray(action.payload.rooms);
        state.roomsData = {
          ...state.roomsData,
          [action.payload.floorId]: rooms
        };
      })
      .addCase(fetchRoomsByFloor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Beds by Room
      .addCase(fetchBedsByRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBedsByRoom.fulfilled, (state, action) => {
        state.loading = false;
        const beds = safeExtractArray(action.payload.beds);
        state.bedsData = {
          ...state.bedsData,
          [action.payload.roomId]: beds
        };
      })
      .addCase(fetchBedsByRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setContactDetails,
  setPatientDetails,
  setInsuranceDetails,
  setPatientMRN,
  setPatientDisplayId,
  setPatientIdentifiers,
  setAddedInsurance,
  setPatientPolicies,
  setSelectedDepartment,
  setSelectedDoctor,
  setConsultationDate,
  setConsultationTime,
  setConsultationType,
  setSelectedSlot,
  setAdmissionType,
  setAdmissionDate,
  setAdmissionReason,
  setAdmissionFee, // ADDED: Export setAdmissionFee
  setSelectedFloorId,
  setSelectedRoomId,
  setSelectedBedId,
  setSelectedRoomCategory,
  setPaymentMode,
  resetConsultation,
  resetPatientStepDetails,
  resetAllBookingData,
  clearError
} = secretaryConsultationSlice.actions;

export default secretaryConsultationSlice.reducer;